/**
 * TTS Service – Kommunikation mit der Google Cloud Text-to-Speech REST API
 */

const API_BASE = 'https://texttospeech.googleapis.com/v1';

/**
 * Synthesizes text to speech audio
 * @param {string} text - Text to synthesize
 * @param {Object} options
 * @param {string} options.apiKey - Google Cloud API key
 * @param {string} options.languageCode - BCP-47 language code (e.g. 'de-DE')
 * @param {string} options.voiceName - Voice name (e.g. 'de-DE-Neural2-B')
 * @param {string} [options.ssmlGender='NEUTRAL'] - Voice gender
 * @param {number} [options.speakingRate=1.0] - Speaking rate (0.25 - 2.0)
 * @param {number} [options.pitch=0] - Pitch (-20.0 to 20.0)
 * @param {string} [options.audioEncoding='MP3'] - Audio encoding
 * @returns {Promise<{audioBlob: Blob, audioUrl: string, charCount: number}>}
 */
export async function synthesize(text, options) {
  const {
    apiKey,
    languageCode,
    voiceName,
    ssmlGender = 'NEUTRAL',
    speakingRate = 1.0,
    pitch = 0,
    audioEncoding = 'MP3',
    signal,
  } = options;

  if (!apiKey) throw new Error('API-Key ist nicht konfiguriert.');
  if (!text?.trim()) throw new Error('Kein Text zum Vorlesen vorhanden.');

  const requestBody = {
    input: { text: text.trim() },
    voice: {
      languageCode,
      name: voiceName,
      ssmlGender,
    },
    audioConfig: {
      audioEncoding,
      speakingRate: parseFloat(speakingRate),
      pitch: parseFloat(pitch),
    },
  };

  const response = await fetch(`${API_BASE}/text:synthesize?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData?.error?.message || `HTTP ${response.status}`;

    if (response.status === 401 || response.status === 403) {
      throw new Error(`Ungültiger API-Key: ${message}`);
    }
    if (response.status === 429) {
      throw new Error(`Rate-Limit überschritten. Bitte warten Sie einen Moment.`);
    }
    throw new Error(`TTS-Fehler: ${message}`);
  }

  const data = await response.json();

  if (!data.audioContent) {
    throw new Error('Keine Audio-Daten in der Antwort.');
  }

  // Decode base64 audio
  const audioBlob = base64ToBlob(data.audioContent, getMimeType(audioEncoding));
  const audioUrl = URL.createObjectURL(audioBlob);

  return {
    audioBlob,
    audioUrl,
    charCount: text.trim().length,
  };
}

/**
 * Fetches available voices from the API
 * @param {string} apiKey
 * @param {string} [languageCode] - Optional language filter
 * @returns {Promise<Array>}
 */
export async function fetchVoices(apiKey, languageCode) {
  if (!apiKey) throw new Error('API-Key ist nicht konfiguriert.');

  const params = new URLSearchParams({ key: apiKey });
  if (languageCode) {
    params.set('languageCode', languageCode);
  }

  const response = await fetch(`${API_BASE}/voices?${params.toString()}`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return (data.voices || []).map((voice) => ({
    name: voice.name,
    languageCodes: voice.languageCodes,
    ssmlGender: voice.ssmlGender,
    naturalSampleRateHertz: voice.naturalSampleRateHertz,
    tier: extractTier(voice.name),
  }));
}

/**
 * Tests the API connection with a minimal request
 * @param {string} apiKey
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function testConnection(apiKey) {
  try {
    const voices = await fetchVoices(apiKey);
    return {
      success: true,
      message: `✓ Verbunden. ${voices.length} Stimmen verfügbar.`,
    };
  } catch (error) {
    return {
      success: false,
      message: `✗ ${error.message}`,
    };
  }
}

/**
 * Extracts the tier (model type) from a voice name
 * @param {string} voiceName
 * @returns {string}
 */
export function extractTier(voiceName) {
  if (!voiceName) return 'Standard';
  const name = voiceName.toLowerCase();
  if (name.includes('chirp3-hd') || name.includes('chirp_3_hd')) return 'Chirp 3 HD';
  if (name.includes('chirp')) return 'Chirp';
  if (name.includes('studio')) return 'Studio';
  if (name.includes('neural2')) return 'Neural2';
  if (name.includes('wavenet')) return 'WaveNet';
  if (name.includes('polyglot')) return 'Polyglot';
  if (name.includes('news')) return 'News';
  return 'Standard';
}

/**
 * Converts a base64 string to a Blob
 * @param {string} base64
 * @param {string} mimeType
 * @returns {Blob}
 */
export function base64ToBlob(base64, mimeType) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

/**
 * Returns the MIME type for an audio encoding
 * @param {string} encoding
 * @returns {string}
 */
export function getMimeType(encoding) {
  const map = {
    MP3: 'audio/mpeg',
    LINEAR16: 'audio/wav',
    OGG_OPUS: 'audio/ogg',
    MULAW: 'audio/basic',
    ALAW: 'audio/alaw',
  };
  return map[encoding] || 'audio/mpeg';
}
